import { Database, supabase } from "..//config/supabase"
import { EmailService } from "./EmailService"
import type { TechCategory } from "../constants/categories"

export type HostsRows = {
  hosts: EventHostRow[]
}
type EventRow = Database['public']['Tables']['events']['Row']
type EventInsert = Database['public']['Tables']['events']['Insert']
type EventUpdate = Database['public']['Tables']['events']['Update']
type EventHostRow = Database['public']['Tables']['event_hosts']['Row']
type EventHostInsert = Database['public']['Tables']['event_hosts']['Insert']

export interface CreateEventData {
  title: string
  short_description?: string
  description?: string
  website_link?: string
  image_url?: string
  start_date: string
  start_time: string
  end_date?: string
  end_time?: string
  time_zone?: string
  location?: string
  location_detail?: string
  category: TechCategory
  ecosystem?: string
  venue?: string
  is_public?: boolean
  requires_approval?: boolean
  limited_places?: number
  parent_event_id?: string // Para side events
  // Nuevos campos para co-hosts
  co_hosts?: string[] // Array de emails de co-hosts,
  waiting_list: boolean
}

export interface CoHostData {
  email: string
  name?: string
  website?: string
}

export interface EventFilters {
  category?: TechCategory
  type?: string
  modality?: string
  is_public?: boolean
  start_date_from?: string
  start_date_to?: string
  created_by?: string
  parent_event_id?: string | null // null para main events, string para side events específicos
}

export interface EventWithRelations extends EventRow {
  waiting_list: boolean
  side_events?: EventRow[]
  parent_event?: EventRow
  hosts_count?: number
  attendees_count?: number
  hosts?: EventHostRow[]
}

export interface EventAttendee {
  id: string;
  event_id: string;
  user_id?: string;
  email: string;
  name?: string;
  avatar_url?: string;
  attendance_status: 'pending' | 'attended' | 'no-show';
  rsvp_status: 'pending' | 'confirmed' | 'declined';
  approval_status: 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_at?: string;
  registered_at: string;
  updated_at?: string;
}

export interface AttendeeFilters {
  rsvp_status?: 'pending' | 'confirmed' | 'declined';
  approval_status?: 'pending' | 'approved' | 'rejected';
  attendance_status?: 'pending' | 'attended' | 'no-show';
  search?: string;
}

export interface BulkInviteData {
  emails: string[];
  event_id: string;
  message?: string;
}

export interface EventUser {
  id: string;
  event_id: string;
  user_id?: string;
  email: string;
  name?: string;
  avatar_url?: string;
  invitation_status: 'pending' | 'invited' | 'accepted' | 'declined';
  approval_status: 'pending' | 'approved' | 'rejected';
  rsvp_status: 'pending' | 'confirmed' | 'declined';
  attendance_status: 'pending' | 'attended' | 'no_show';
  is_on_waiting_list: boolean;
  waiting_list_position?: number;
  invited_at?: string;
  responded_at?: string;
  approved_at?: string;
  approved_by?: string;
  registered_at: string;
  updated_at?: string;
  invitation_token?: string;
  invitation_message?: string;
  invited_by?: string;
}

export interface EventUserFilters {
  invitation_status?: 'pending' | 'invited' | 'accepted' | 'declined';
  approval_status?: 'pending' | 'approved' | 'rejected';
  rsvp_status?: 'pending' | 'confirmed' | 'declined';
  attendance_status?: 'pending' | 'attended' | 'no_show';
  is_on_waiting_list?: boolean;
  search?: string;
}

export class Event {
  /**
   * Crear un nuevo evento con co-hosts
   */
  static async create(eventData: CreateEventData, userId: string, userProfile?: { name?: string; email?: string, avatarUrl: string }): Promise<{ data: EventRow | null; error: any; hosts?: EventHostRow[] }> {
    try {
      // Iniciar una transacción usando múltiples operaciones
      
      // 1. Crear el evento
      const { data: event, error: eventError } = await supabase
        .from('events')
        .insert({
          title: eventData.title,
          short_description: eventData.short_description,
          description: eventData.description,
          website_link: eventData.website_link,
          image_url: eventData.image_url,
          start_date: eventData.start_date,
          start_time: eventData.start_time,
          end_date: eventData.end_date,
          end_time: eventData.end_time,
          time_zone: eventData.time_zone,
          location: eventData.location,
          location_detail: eventData.location_detail,
          category: eventData.category,
          is_public: eventData.is_public,
          requires_approval: eventData.requires_approval,
          limited_places: eventData.limited_places,
          parent_event_id: eventData.parent_event_id,
          created_by: userId,
          waiting_list: eventData.waiting_list
        })
        .select()
        .single()

      if (eventError) {
        return { data: null, error: eventError }
      }

      // 2. Crear el host principal (el creador del evento)
      const mainHostData: EventHostInsert = {
        event_id: event.id,
        user_id: userId,
        email: userProfile?.email || '',
        name: userProfile?.name || 'Event Creator',
        is_main_host: true,
        avatar_url: userProfile?.avatarUrl || null,
        status: 'accepted'
      }

      const { data: mainHost, error: mainHostError } = await supabase
        .from('event_hosts')
        .insert(mainHostData)
        .select()
        .single()

      if (mainHostError) {
        console.error('Error creating main host:', mainHostError)
        // Si falla la creación del host principal, eliminamos el evento
        await supabase.from('events').delete().eq('id', event.id)
        return { data: null, error: mainHostError }
      }

      const allHosts = [mainHost]

      // La lógica de creación de co-hosts se ha eliminado de aquí.
      // El frontend ahora debe llamar a `addCoHosts` por separado después de crear el evento.

      return { data: event, error: null, hosts: allHosts }
    } catch (error) {
      console.error('Unexpected error in create:', error)
      return { data: null, error }
    }
  }

  /**
   * Agregar co-hosts a un evento existente, creando el registro y enviando la invitación.
   */
  static async addCoHosts(eventId: string, coHosts: CoHostData[], userId: string): Promise<{ data: EventHostRow[] | null; error: any }> {
    try {
      // 1. Verificar que el usuario tiene permisos para agregar co-hosts
      const { data: existingEvent, error: fetchError } = await supabase
        .from('events')
        .select('created_by')
        .eq('id', eventId)
        .single()

      if (fetchError) {
        return { data: null, error: fetchError }
      }

      if (existingEvent.created_by !== userId) {
        return { data: null, error: { message: 'You do not have permission to add co-hosts to this event' } }
      }

      // 2. Preparar datos de co-hosts
      const coHostsData: EventHostInsert[] = coHosts.map(coHost => ({
        event_id: eventId,
        email: coHost.email.trim(),
        name: coHost.name || coHost.email.split('@')[0],
        website: coHost.website,
        is_main_host: false,
        status: 'pending',
        invited_by: userId,
        invited_at: new Date().toISOString()
      }))

      // 3. Insertar los nuevos co-hosts
      const { data: newHosts, error: insertError } = await supabase
        .from('event_hosts')
        .insert(coHostsData)
        .select()

      if (insertError) {
        return { data: null, error: insertError }
      }

      // 4. Enviar invitaciones por correo para cada nuevo co-host
      for (const host of newHosts) {
        try {
          // Generar token
          const { data: tokenData, error: tokenError } = await EmailService.generateCoHostToken(
            eventId,
            host.email,
            host.name || ''
          );

          if (tokenError) {
            console.error(`Failed to generate token for ${host.email}:`, tokenError);
            continue; // Skip to next host
          }

          // Enviar email
          await EmailService.sendCoHostInvitation(
            eventId,
            host.email,
            host.name || '',
            tokenData.token
          );
        } catch (emailError) {
          console.error(`Failed to send invitation email to ${host.email}:`, emailError);
          // The host is created, but the email failed. The admin can resend it.
        }
      }

      return { data: newHosts, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  /**
   * Obtener hosts de un evento
   */
  static async getEventHosts(eventId: string): Promise<{ data: EventHostRow[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('event_hosts')
        .select('*')
        .eq('event_id', eventId)
        .order('is_main_host', { ascending: false }) // Main host primero

      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  /**
   * Actualizar estado de un co-host (aceptar/rechazar invitación)
   */
  static async updateHostStatus(hostId: string, status: 'accepted' | 'declined', hostData?: { name?: string; website?: string }): Promise<{ data: EventHostRow | null; error: any }> {
    try {
      const updateData: any = {
        status,
        responded_at: new Date().toISOString()
      }

      if (hostData?.name) {
        updateData.name = hostData.name
      }

      if (hostData?.website) {
        updateData.website = hostData.website
      }

      const { data, error } = await supabase
        .from('event_hosts')
        .update(updateData)
        .eq('id', hostId)
        .select()
        .single()

      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  /**
   * Eliminar un co-host
   */
  static async removeCoHost(hostId: string, userId: string): Promise<{ error: any }> {
    try {
      // 1. Obtener la información del host
      const { data: host, error: fetchHostError } = await supabase
        .from('event_hosts')
        .select('id, event_id, user_id, is_main_host')
        .eq('id', hostId)
        .single();

      if (fetchHostError) {
        return { error: { message: 'Co-host not found.', details: fetchHostError } };
      }

      // No permitir eliminar al host principal
      if (host.is_main_host) {
        return { error: { message: 'Cannot remove the main host' } };
      }

      // 2. Obtener la información del evento para verificar al creador
      const { data: event, error: fetchEventError } = await supabase
        .from('events')
        .select('created_by')
        .eq('id', host.event_id)
        .single();

      if (fetchEventError) {
        return { error: { message: 'Event not found for this co-host.', details: fetchEventError } };
      }

      // 3. Verificar permisos: solo el creador del evento o el mismo co-host pueden eliminar
      const isEventCreator = event.created_by === userId;
      const isSelf = host.user_id === userId;

      if (!isEventCreator && !isSelf) {
        return { error: { message: 'You do not have permission to remove this co-host' } };
      }

      // 4. Eliminar el co-host
      const { error: deleteError } = await supabase
        .from('event_hosts')
        .delete()
        .eq('id', hostId);

      if (deleteError) {
        return { error: deleteError };
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  }

  /**
   * Actualizar un evento existente
   */
  static async update(eventId: string, eventData: Partial<CreateEventData>, userId: string): Promise<{ data: EventRow | null; error: any }> {
    try {
      const { data: existingEvent, error: fetchError } = await supabase
        .from('events')
        .select('created_by')
        .eq('id', eventId)
        .single()

      if (fetchError) {
        return { data: null, error: fetchError }
      }

      if (existingEvent.created_by !== userId) {
        return { data: null, error: { message: 'You do not have permission to update this event' } }
      }

      // Extraer co_hosts del eventData para manejo separado
      const { co_hosts, ...eventUpdateData } = eventData

      const { data, error } = await supabase
        .from('events')
        .update(eventUpdateData)
        .eq('id', eventId)
        .select()
        .single()

      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  /**
   * Obtener un evento por ID con relaciones incluyendo hosts
   */
  static async getById(eventId: string): Promise<{ data: EventWithRelations | null; error: any }> {
    try {
      // Primero obtener el evento básico
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (eventError) {
        return { data: null, error: eventError };
      }

      if (!event) {
        return { data: null, error: { message: 'Event not found' } };
      }

      // Obtener los hosts por separado
      const { data: hosts, error: hostsError } = await supabase
        .from('event_hosts')
        .select('*')
        .eq('event_id', eventId);

      if (hostsError) {
        console.warn('Error fetching hosts:', hostsError);
        // No fallar por error de hosts, continuar sin ellos
      }

      // Obtener side events si existe parent_event_id en la tabla
      let sideEvents: EventRow[] | null = null;
      try {
        const { data: sideEventsData, error: sideEventsError } = await supabase
          .from('events')
          .select('*')
          .eq('parent_event_id', eventId);

        if (!sideEventsError) {
          sideEvents = sideEventsData;
        }
      } catch (error) {
        console.warn('Side events relation not available:', error);
        // Continuar sin side events
      }

      // Obtener parent event si este evento tiene parent_event_id
      let parentEvent: EventRow | null = null;
      if (event.parent_event_id) {
        try {
          const { data: parentEventData, error: parentError } = await supabase
            .from('events')
            .select('*')
            .eq('id', event.parent_event_id)
            .single();

          if (!parentError) {
            parentEvent = parentEventData;
          }
        } catch (error) {
          console.warn('Parent event relation not available:', error);
          // Continuar sin parent event
        }
      }

      // Construir el objeto con relaciones
      const eventWithRelations: EventWithRelations = {
        ...event,
        hosts: hosts || [],
        side_events: sideEvents || [],
        parent_event: parentEvent || undefined,
        hosts_count: hosts?.length || 0,
        attendees_count: 0 // TODO: Implementar cuando tengamos tabla de attendees
      };

      return { data: eventWithRelations, error: null };
    } catch (error) {
      console.error('Error in getById:', error);
      return { data: null, error };
    }
  }

  /**
   * Obtener evento crudo (toda la fila) por ID
   */
  static async getRaw(eventId: string): Promise<{ data: any | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Obtener eventos con filtros
   */
  static async getEvents(filters: EventFilters = {}, limit = 20, offset = 0): Promise<{ data: EventWithRelations[] | null; error: any }> {
    try {
      let query = supabase
        .from('events')
        .select('*');

      // Aplicar filtros
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.modality) {
        query = query.eq('modality', filters.modality);
      }
      if (filters.is_public !== undefined) {
        query = query.eq('is_public', filters.is_public);
      }
      if (filters.created_by) {
        query = query.eq('created_by', filters.created_by);
      }
      if (filters.start_date_from) {
        query = query.gte('start_date', filters.start_date_from);
      }
      if (filters.start_date_to) {
        query = query.lte('start_date', filters.start_date_to);
      }
      // Solo aplicar filtro de parent_event_id si existe la columna
      if (filters.parent_event_id !== undefined) {
        try {
          if (filters.parent_event_id === null) {
            query = query.is('parent_event_id', null);
          } else {
            query = query.eq('parent_event_id', filters.parent_event_id);
          }
        } catch (error) {
          console.warn('parent_event_id filter not available:', error);
        }
      }

      query = query
        .order('start_date', { ascending: true })
        .range(offset, offset + limit - 1);

      const { data: events, error: eventsError } = await query;

      if (eventsError) {
        return { data: null, error: eventsError };
      }

      if (!events || events.length === 0) {
        return { data: [], error: null };
      }

      // Obtener hosts para todos los eventos
      const eventIds = events.map(event => event.id);
      const { data: allHosts } = await supabase
        .from('event_hosts')
        .select('*')
        .in('event_id', eventIds);

      // Construir eventos con relaciones
      const eventsWithRelations: EventWithRelations[] = events.map(event => ({
        ...event,
        hosts: allHosts?.filter(host => host.event_id === event.id) || [],
        side_events: [], // Se puede cargar bajo demanda
        hosts_count: allHosts?.filter(host => host.event_id === event.id).length || 0,
        attendees_count: 0 // TODO: Implementar cuando tengamos tabla de attendees
      }));
      
      return { data: eventsWithRelations, error: null };
    } catch (error) {
      console.error('Error in getEvents:', error);
      return { data: null, error };
    }
  }

  /**
   * Obtener eventos creados por un usuario
   */
  static async getByUser(userId: string, includesSideEvents = true, limit = 500, offset = 0): Promise<{ data: EventWithRelations[] | null; error: any }> {
    const filters: EventFilters = {
      created_by: userId
    }

    if (!includesSideEvents) {
      filters.parent_event_id = null
    }

    return this.getEvents(filters, limit, offset)
  }

  /**
   * Obtener eventos a los que un usuario asiste
   */
  static async getAttendingEvents(userId: string): Promise<{ data: EventWithRelations[] | null; error: any }> {
    try {
      // Obtener los registros de asistencia donde el usuario ha confirmado
      const { data: attendeeRecords, error } = await supabase
        .from('event_helpers')
        .select(`
          event:events (
            *
          )
        `)
        .eq('user_id', userId)
        .eq('rsvp_status', 'confirmed')

      if (error) {
        console.error('Error fetching attending events:', error)
        return { data: null, error }
      }

      if (!attendeeRecords) {
        return { data: [], error: null }
      }

      // Mapear los resultados para construir el objeto EventWithRelations
      const eventsWithRelations: EventWithRelations[] = attendeeRecords
        .map(record => {
          if (!record.event) return null

          const eventData = record.event as any

          return {
            ...eventData,
            hosts: eventData.hosts || [],
            side_events: [], // Se pueden cargar bajo demanda si es necesario
            hosts_count: eventData.hosts?.length || 0,
            attendees_count: 0 // TODO: Implementar
          }
        })
        .filter((event): event is EventWithRelations => event !== null)

      // Filtrar eventos duplicados que puedan surgir de múltiples registros de asistencia para el mismo evento
      const uniqueEvents = Array.from(new Map(eventsWithRelations.map(event => [event.id, event])).values())
        .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())

      return { data: uniqueEvents, error: null }
    } catch (error) {
      console.error('Error in getAttendingEvents:', error)
      return { data: null, error }
    }
  }

  /**
   * Obtener eventos donde el usuario es co-host (no main host)
   */
  static async getCoHostedEvents(userId: string): Promise<{ data: EventWithRelations[] | null; error: any }> {
    try {
      const { data: cohostRelations, error: coHostError } = await supabase
        .from('event_hosts')
        .select('event_id')
        .eq('user_id', userId)
        .eq('is_main_host', false)
        .eq('status', 'accepted')
      
      if (coHostError) {
        return { data: null, error: coHostError }
      }

      const ids = Array.from(new Set((cohostRelations || []).map((r: any) => r.event_id).filter(Boolean)))
      if (ids.length === 0) return { data: [], error: null }

      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .in('id', ids)
        .order('start_date', { ascending: true })
      
      if (eventsError) {
        return { data: null, error: eventsError }
      }

      // Cargar hosts para esas IDs
      const { data: allHosts } = await supabase
        .from('event_hosts')
        .select('*')
        .in('event_id', ids)

      const eventsWithRelations: EventWithRelations[] = (events || []).map((e: any) => ({
        ...e,
        hosts: allHosts?.filter(h => h.event_id === e.id) || [],
        side_events: [],
        hosts_count: allHosts?.filter(h => h.event_id === e.id).length || 0,
        attendees_count: 0,
      }))

      return { data: eventsWithRelations, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  

  /**
   * Obtener side events de un evento principal
   */
  static async getSideEvents(mainEventId: string): Promise<{ data: any[] | null; error: any }> {
    try {
      // First get the basic side event data
      const { data: sideEvents, error } = await supabase
        .from('events')
        .select('*')
        .eq('parent_event_id', mainEventId)
        .order('start_date', { ascending: true });

      if (error) {
        return { data: null, error };
      }

      if (!sideEvents || sideEvents.length === 0) {
        return { data: [], error: null };
      }

      // Get all hosts for all side events in one query
      const sideEventIds = sideEvents.map(event => event.id);
      const { data: allHosts, error: hostsError } = await supabase
        .from('event_hosts')
        .select('*')
        .in('event_id', sideEventIds);

      if (hostsError) {
        console.warn('Error fetching hosts for side events:', hostsError);
      }

      // Add hosts to their respective events
      const sideEventsWithHosts = sideEvents.map(event => {
        const eventHosts = allHosts?.filter(host => host.event_id === event.id) || [];
        return {
          ...event,
          hosts: eventHosts
        };
      });

      return { data: sideEventsWithHosts, error: null };
    } catch (error) {
      console.warn('Side events functionality not available:', error);
      return { data: [], error: null };
    }
  }

  /**
   * Eliminar un evento
   */
  static async delete(eventId: string, userId: string): Promise<{ error: any }> {
    try {
      // Verificar que el usuario es el creador del evento
      const { data: existingEvent, error: fetchError } = await supabase
        .from('events')
        .select('created_by')
        .eq('id', eventId)
        .single()

      if (fetchError) {
        return { error: fetchError }
      }

      if (existingEvent.created_by !== userId) {
        return { error: { message: 'No tienes permisos para eliminar este evento' } }
      }

      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId)

      return { error }
    } catch (error) {
      return { error }
    }
  }

  /**
   * Buscar eventos por texto
   */
  static async search(searchTerm: string, filters: EventFilters = {}): Promise<{ data: EventWithRelations[] | null; error: any }> {
    try {
      let query = supabase
        .from('events')
        .select(`
          *,
          side_events:events!parent_event_id(id, title, start_date, start_time)
        `)
        .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`)

      // Aplicar filtros adicionales
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'parent_event_id' && value === null) {
            query = query.is('parent_event_id', null)
          } else {
            query = query.eq(key, value)
          }
        }
      })

      const { data, error } = await query
        .order('start_date', { ascending: true })
        .limit(50)

      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  /**
   * Obtener eventos públicos próximos para la landing page.
   */
static async getUpcoming(limit = 10): Promise<{ data: EventWithRelations[] | null; error: any }> {
    try {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const today = `${yyyy}-${mm}-${dd}`; // YYYY-MM-DD
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      const currentTime = `${hh}:${min}`; // HH:mm

      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('*, parent_event:events!parent_event_id(title)')
        .eq('is_public', true)
        .or(`start_date.gt.${today},and(start_date.eq.${today},start_time.gte.${currentTime})`)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true })
        .limit(limit);

      if (eventsError) {
        return { data: null, error: eventsError };
      }

      if (!events || events.length === 0) {
        return { data: [], error: null };
      }

      // Obtener hosts para todos los eventos
      const eventIds = events.map(e => e.id);
      const { data: allHosts } = await supabase
        .from('event_hosts')
        .select('*')
        .in('event_id', eventIds);
      // Construir eventos con relaciones (incluye hosts)
      const eventsWithRelations: EventWithRelations[] = (events as any[]).map(event => ({
        ...event,
        hosts: allHosts?.filter(h => h.event_id === event.id) || [],
        side_events: [],
        hosts_count: allHosts?.filter(h => h.event_id === event.id).length || 0,
      }));

      return { data: eventsWithRelations, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Helper interno para slugificar igual que frontend
  private static slugifyTitle(title: string): string {
    return (title || '')
      .toLowerCase()
      .normalize('NFD')                // separa acentos
      .replace(/\p{Diacritic}/gu, '')  // quita acentos
      .replace(/@/g, 'at')             // opcional: tratar @ de forma estable
      .replace(/[^a-z0-9]+/g, '-')     // separadores
      .replace(/^-+|-+$/g, '')         // bordes
      .replace(/-{2,}/g, '-');         // múltiple a uno
  }

  /**
   * Get event by slug (robusto ante múltiples coincidencias y caracteres especiales)
   */
  static async getBySlug(slug: string): Promise<{ data: EventWithRelations | null; error: any }> {
    try {
      if (!slug) return { data: null, error: { message: 'Invalid slug' } };

      const decodedSlug = decodeURIComponent(slug).trim().toLowerCase();

      // Patrón ILIKE amplio: %palabra1%palabra2%...
      const parts = decodedSlug.split('-').filter(Boolean);
      const likePattern = parts.length
        ? `%${parts.map(p => p.replace(/[%_]/g, '')).join('%')}%`
        : `%${decodedSlug.replace(/[%_]/g, '')}%`;

      // Traer posibles candidatos (limit defensivo)
      const { data: candidates, error: queryError } = await supabase
        .from('events')
        .select('id,title,is_public')
        .eq('is_public', true)
        .ilike('title', likePattern)
        .limit(20);

      if (queryError) {
        console.error('Error in slug search:', queryError);
        return { data: null, error: queryError };
      }

      let pool = candidates || [];
      // Fallback para acentos: si no hay candidatos por ILIKE (que es acento-sensible en muchas configs),
      // traemos un pool más amplio y filtramos por slug en JS (hasta 500 eventos públicos)
      if (!pool || pool.length === 0) {
        const { data: wide } = await supabase
          .from('events')
          .select('id,title,is_public')
          .eq('is_public', true)
          .order('start_date', { ascending: true })
          .limit(500);
        pool = wide || [];
      }

      // Filtrar por slug exacto calculado
      const exactMatches = pool.filter(e => this.slugifyTitle(e.title) === decodedSlug);

      let chosen = exactMatches[0];

      // Si no hay exacto, intentar fallback mejor heurística (el que tenga mayor intersección de partes)
      if (!chosen) {
        const scored = pool.map(e => {
          const tSlug = this.slugifyTitle(e.title);
            const score = parts.filter(p => tSlug.includes(p)).length;
          return { e, score, lenDiff: Math.abs(tSlug.length - decodedSlug.length) };
        }).sort((a,b) => b.score - a.score || a.lenDiff - b.lenDiff);
        chosen = scored[0]?.e;
      }

      if (!chosen) {
        return { data: null, error: { message: 'Event not found' } };
      }

      return await this.getById(chosen.id);
    } catch (error) {
      console.error('Error in getBySlug:', error);
      return { data: null, error };
    }
  }

  /**
   * Link an existing event as a side event to a main event
   */
  static async linkAsSideEvent(eventIdToLink: string, mainEventId: string, userId: string): Promise<{ data: EventRow | null; error: any }> {
    try {
      // Verify the user has permission to modify the event to be linked
      const { data: eventToLink, error: fetchEventError } = await supabase
        .from('events')
        .select('created_by, parent_event_id')
        .eq('id', eventIdToLink)
        .single();

      if (fetchEventError) {
        return { data: null, error: fetchEventError };
      }

      if (eventToLink.created_by !== userId) {
        return { data: null, error: { message: 'You do not have permission to modify this event' } };
      }
      
      // Check if the event is already a side event
      if (eventToLink.parent_event_id) {
        return { data: null, error: { message: 'This event is already linked to another main event' } };
      }

      // Update the event, setting it as a side event
      const { data, error } = await supabase
        .from('events')
        .update({ parent_event_id: mainEventId })
        .eq('id', eventIdToLink)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Unlink a side event from its main event
   */
  static async unlinkSideEvent(sideEventId: string, userId: string): Promise<{ data: EventRow | null; error: any }> {
    try {
      // Verify the user has permission to modify the side event
      const { data: sideEvent, error: fetchEventError } = await supabase
        .from('events')
        .select('created_by, parent_event_id')
        .eq('id', sideEventId)
        .single();

      if (fetchEventError) {
        return { data: null, error: fetchEventError };
      }

      if (sideEvent.created_by !== userId) {
        return { data: null, error: { message: 'You do not have permission to modify this event' } };
      }
      
      // Check if the event is actually a side event
      if (!sideEvent.parent_event_id) {
        return { data: null, error: { message: 'This event is not a side event' } };
      }

      // Update the event, removing the parent_event_id
      const { data, error } = await supabase
        .from('events')
        .update({ parent_event_id: null })
        .eq('id', sideEventId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Search for events that could be linked as side events
   * Returns events that are not already side events and match the search term
   */
  static async searchPotentialSideEvents(searchTerm: string, userId: string): Promise<{ data: EventRow[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('created_by', userId) // Only events created by this user
        .is('parent_event_id', null) // Only main events (not already side events)
        .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
        .order('start_date', { ascending: false })
        .limit(20);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Get attendees for an event with optional filters
   */
  static async getAttendees(
    eventId: string,
    filters?: AttendeeFilters
  ): Promise<{ data: EventAttendee[] | null; error: any }> {
    try {
      let query = supabase
        .from('event_helpers')
        .select('*')
        .eq('event_id', eventId);

      // Apply filters if provided
      if (filters?.rsvp_status) {
        query = query.eq('rsvp_status', filters.rsvp_status);
      }
      
      if (filters?.approval_status) {
        query = query.eq('approval_status', filters.approval_status);
      }
      
      if (filters?.attendance_status) {
        query = query.eq('attendance_status', filters.attendance_status);
      }

      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query.order('registered_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('Error fetching attendees:', error);
      return { data: null, error };
    }
  }

  /**
   * Invite a single attendee to an event
   */
  static async inviteAttendee(
    eventId: string,
    attendeeData: { email: string; name?: string },
    inviterId: string
  ): Promise<{ data: EventAttendee | null; error: any }> {
    try {
      // Check if the attendee is already invited
      const { data: existingAttendee } = await supabase
        .from('event_helpers')
        .select('*')
        .eq('event_id', eventId)
        .eq('email', attendeeData.email)
        .maybeSingle();

      if (existingAttendee) {
        return { 
          data: null, 
          error: { message: 'This email address has already been invited' } 
        };
      }

      // Get event details for email invitation
      const { data: eventDetails } = await this.getById(eventId);
      if (!eventDetails) {
        return { data: null, error: { message: 'Event not found' } };
      }

      // Determine if event requires approval
      const requiresApproval = eventDetails.requires_approval === true;
      
      // Create attendee record
      const { data: attendee, error } = await supabase
        .from('event_helpers')
        .insert([
          {
            event_id: eventId,
            email: attendeeData.email,
            name: attendeeData.name || attendeeData.email.split('@')[0],
            user_id: null, // Will be linked if user registers/logs in
            rsvp_status: 'pending',
            approval_status: requiresApproval ? 'pending' : 'approved',
            attendance_status: 'pending',
            registered_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (error) {
        return { data: null, error };
      }

      // Get the host info for the email
      const { data: hosts } = await supabase
        .from('event_hosts')
        .select('name')
        .eq('event_id', eventId)
        .eq('is_main_host', true)
        .single();
      
      const hostName = hosts?.name || 'Event Host';

      // Generate invitation token and store it
      const { error: tokenError, managerToken } = await EmailService.sendEventAttendanceInvitation(
        attendeeData.email,
        {
          id: eventDetails.id,
          title: eventDetails.title,
          start_date: eventDetails.start_date,
          start_time: eventDetails.start_time || '00:00',
          end_date: eventDetails.end_date || '00:00',
          end_time: eventDetails.end_time || '00:00',
          location: eventDetails.location || 'Online',
          type: eventDetails.type || 'General',
          category: eventDetails.category || 'Event',
          modality: eventDetails.modality || 'in-person',
          host_name: hostName,
          image_url: eventDetails.image_url || '',
          base_url: 'https://cap.com'
        }
      );

      if (tokenError) {
        console.error("Error sending invitation email:", tokenError);
        // Continue even if email fails - the attendee is still created
      }

      // Link the attendee record with the invitation token
      if (managerToken) {
        await supabase
          .from('invitation_tokens')
          .update({ attendee_id: attendee.id })
          .eq('token', managerToken);
      }

      return { data: attendee, error: null };
    } catch (error) {
      console.error('Error inviting attendee:', error);
      return { data: null, error };
    }
  }

  /**
   * Accept an event invitation, linking the user and confirming attendance.
   */
  static async acceptInvitation(
    eventId: string,
    email: string,
    user: { id: string; name?: string; avatar_url?: string }
  ): Promise<{ data: EventAttendee | null; error: any }> {
    try {
      // 1. Find the attendee record by email and eventId
      const { data: existingAttendee, error: findError } = await supabase
        .from('event_helpers')
        .select('*')
        .eq('event_id', eventId)
        .eq('rsvp_status', 'pending') // Only consider pending invitations
        .eq('email', email)
        .maybeSingle();

      console.log('Existing Attendee:', existingAttendee);
      if (findError) {
        console.error('Error finding attendee record:', findError);
        return { data: null, error: findError };
      }

      if (!existingAttendee) {
        return { data: null, error: { message: 'Invitation not found for this email address.' } };
      }

      // 2. Check if the user is already fully registered and confirmed.
      if (existingAttendee.user_id === user.id && existingAttendee.rsvp_status === 'confirmed') {
          return { data: existingAttendee, error: { message: 'You are already confirmed for this event.' } };
      }

      // 3. Prepare update data
      const updateData: Partial<EventAttendee> = {
        user_id: user.id,
        rsvp_status: 'confirmed',
        attendance_status: 'attended', // Confirm attendance
        updated_at: new Date().toISOString(),
        
      };

      // Update name and avatar if they are available from the user profile
      if (user.name) {
          updateData.name = user.name;
      }
      if (user.avatar_url) {
          updateData.avatar_url = user.avatar_url;
      }

      // 4. Update the attendee record
      const { data: updatedAttendee, error: updateError } = await supabase
        .from('event_helpers')
        .update(updateData)
        .eq('id', existingAttendee.id).select()
      .maybeSingle(); // Use maybeSingle() to prevent error if RLS hides the result

      if (updateError) {
        console.error('Error updating attendee record:', updateError);
        return { data: null, error: updateError };
      }

      if (!updatedAttendee) {
        // This can happen if RLS prevents the SELECT part of the update.
        // We assume the update was successful and return the merged data.
        console.warn('Could not retrieve the updated attendee record, likely due to RLS policies. Assuming the update was successful.');
        return { data: { ...existingAttendee, ...updateData } as EventAttendee, error: null };
      }

      return { data: updatedAttendee, error: null };

    } catch (error) {
      console.error('Error in acceptInvitation:', error);
      return { data: null, error };
    }
  }

  /**
   * Invite multiple attendees to an event
   */
  static async bulkInviteAttendees(
    eventId: string,
    data: BulkInviteData,
    inviterId: string
  ): Promise<{ successful: number; failed: number; error: any }> {
    try {
      const results = await Promise.all(
        data.emails.map(email => 
          this.inviteAttendee(eventId, { email }, inviterId)
        )
      );

      const successful = results.filter(result => !result.error).length;
      const failed = results.filter(result => result.error).length;

      return { successful, failed, error: null };
    } catch (error) {
      console.error('Error in bulk invite:', error);
      return { successful: 0, failed: data.emails.length, error };
    }
  }

  /**
   * Update attendee RSVP status (confirm/decline)
   */
  static async updateAttendeeRsvp(
    attendeeId: string,
    rsvpStatus: 'confirmed' | 'declined',
  ): Promise<{ data: EventAttendee | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('event_helpers')
        .update({ 
          rsvp_status: rsvpStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', attendeeId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Update attendee approval status
   */
  static async approveAttendee(
    attendeeId: string,
    approvalStatus: 'approved' | 'rejected',
    userId: string
  ): Promise<{ data: EventAttendee | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('event_helpers')
        .update({ 
          approval_status: approvalStatus,
          approved_by: userId,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', attendeeId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Mark attendee attendance status (attended/no-show)
   */
  static async markAttendance(
    attendeeId: string,
    attendanceStatus: 'attended' | 'no-show'
  ): Promise<{ data: EventAttendee | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('event_helpers')
        .update({ 
          attendance_status: attendanceStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', attendeeId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Get attendance statistics
   */
  static async getAttendanceStats(eventId: string): Promise<{ 
    total: number;
    rsvp: {
      confirmed: number;
      pending: number;
      declined: number;
    };
    attendance: {
      attended: number;
      noShow: number;
      pending: number;
    };
    approval: {
      approved: number;
      pending: number;
      rejected: number;
    };
    error: any;
  }> {
    try {
      const { data, error } = await supabase
        .from('event_helpers')
        .select('rsvp_status, attendance_status, approval_status')
        .eq('event_id', eventId);

      if (error) {
        return { 
          total: 0, 
          rsvp: { confirmed: 0, pending: 0, declined: 0 },
          attendance: { attended: 0, noShow: 0, pending: 0 },
          approval: { approved: 0, pending: 0, rejected: 0 },
          error 
        };
      }

      const stats = {
        total: data.length,
        rsvp: {
          confirmed: data.filter(a => a.rsvp_status === 'confirmed').length,
          pending: data.filter(a => a.rsvp_status === 'pending').length,
          declined: data.filter(a => a.rsvp_status === 'declined').length,
        },
        attendance: {
          attended: data.filter(a => a.attendance_status === 'attended').length,
          noShow: data.filter(a => a.attendance_status === 'no-show').length,
          pending: data.filter(a => a.attendance_status === 'pending').length,
        },
        approval: {
          approved: data.filter(a => a.approval_status === 'approved').length,
          pending: data.filter(a => a.approval_status === 'pending').length,
          rejected: data.filter(a => a.approval_status === 'rejected').length,
        },
        error: null
      };

      return stats;
    } catch (error) {
      console.error('Error getting attendance stats:', error);
      return { 
        total: 0, 
        rsvp: { confirmed: 0, pending: 0, declined: 0 },
        attendance: { attended: 0, noShow: 0, pending: 0 },
        approval: { approved: 0, pending: 0, rejected: 0 },
        error 
      };
    }
  }

  /**
   * Remove an attendee from an event
   */
  static async removeAttendee(
    attendeeId: string,
    userId: string
  ): Promise<{ data: boolean | null; error: any }> {
    try {
      // First check if the user has permission to remove this attendee
      const { data: attendee, error: findError } = await supabase
        .from('event_helpers')
        .select('*, events!inner(*)')
        .eq('id', attendeeId)
        .single();

      if (findError || !attendee) {
        return { data: null, error: { message: 'Attendee not found' } };
      }

      // Check if user is the event host or the attendee themselves
      const { data: isHost, error: hostError } = await supabase
        .from('event_hosts')
        .select('id')
        .eq('event_id', attendee.event_id)
        .eq('user_id', userId)
        .eq('is_main_host', true)
        .single();

      const isAttendeeThemselves = attendee.user_id === userId;

      if (!isHost && !isAttendeeThemselves) {
        return { data: null, error: { message: 'You do not have permission to remove this attendee' } };
      }

      // Remove the attendee
      const { error: deleteError } = await supabase
        .from('event_helpers')
        .delete()
        .eq('id', attendeeId);

      if (deleteError) {
        console.error('Error removing attendee:', deleteError);
        return { data: null, error: deleteError };
      }

      return { data: true, error: null };

    } catch (error) {
      console.error('Error in removeAttendee:', error);
      return { data: null, error };
    }
  }

  /**
   * Check user permissions for an event
   */
  static async getUserEventPermissions(
    eventId: string,
    userId: string
  ): Promise<{
    canView: boolean;
    canEdit: boolean;
    canManageAttendees: boolean;
    role: 'creator' | 'host' | 'helper' | 'none';
    error: any;
  }> {
    try {
      // Get event basic info
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('created_by, is_public')
        .eq('id', eventId)
        .single();

      if (eventError) {
        return {
          canView: false,
          canEdit: false,
          canManageAttendees: false,
          role: 'none',
          error: eventError
        };
      }

      if (!event) {
        return {
          canView: false,
          canEdit: false,
          canManageAttendees: false,
          role: 'none',
          error: { message: 'Event not found' }
        };
      }

      // Check if user is the creator
      if (event.created_by === userId) {
        return {
          canView: true,
          canEdit: true,
          canManageAttendees: true,
          role: 'creator',
          error: null
        };
      }

      // Check if user is a host
      const { data: hostRecord } = await supabase
        .from('event_hosts')
        .select('is_main_host, status')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .eq('status', 'accepted')
        .single();

      if (hostRecord) {
        return {
          canView: true,
          canEdit: hostRecord.is_main_host,
          canManageAttendees: true,
          role: 'host',
          error: null
        };
      }

      // Check if user is an attendee
      const { data: helperRecord } = await supabase
        .from('event_helpers')
        .select('rsvp_status')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .eq('rsvp_status', 'confirmed')
        .single();

      if (helperRecord) {
        return {
          canView: true,
          canEdit: false,
          canManageAttendees: false,
          role: 'helper',
          error: null
        };
      }

      // Check if event is public
      if (event.is_public) {
        return {
          canView: true,
          canEdit: false,
          canManageAttendees: false,
          role: 'none',
          error: null
        };
      }

      // No access
      return {
        canView: false,
        canEdit: false,
        canManageAttendees: false,
        role: 'none',
        error: { message: 'You do not have permission to view this event' }
      };

    } catch (error) {
      console.error('Error checking user permissions:', error);
      return {
        canView: false,
        canEdit: false,
        canManageAttendees: false,
        role: 'none',
        error
      };
    }
  }

  /**
   * Get co-hosts for an event (excluding main host)
   */
  static async getCoHosts(eventId: string): Promise<{ data: any[] | null, error: any }> {
    try {
      const { data, error } = await supabase
        .from('event_hosts')
        .select(`
          id,
          email,
          name,
          avatar_url,
          status,
          invited_at,
          responded_at,
          user_id,
          website
        `)
        .eq('event_id', eventId)
        .eq('is_main_host', false) // Only co-hosts, not main host
        .order('invited_at', { ascending: false });

      if (error) {
        return { data: null, error };
      }

      // Transform the data to match our interface
      const coHosts = data?.map(item => ({
        id: item.id,
        name: item.name || item.email.split('@')[0],
        email: item.email,
        avatar: item.avatar_url,
        status: item.status,
        invited_at: item.invited_at,
        responded_at: item.responded_at,
        user_id: item.user_id,
        website: item.website
      })) || [];

      return { data: coHosts, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Accept co-host invitation
   */
  static async acceptCoHostInvitation(
    eventId: string,
    userEmail: string,
    userProfile: { id: string, name?: string, avatar_url?: string }
  ): Promise<{ error: any }> {
    try {
      // Check if user is already a co-host
      const { data: existing } = await supabase
        .from('event_hosts')
        .select('*')
        .eq('event_id', eventId)
        .eq('email', userEmail)
        .single();

      if (existing) {
        if (existing.status === 'accepted') {
          return { error: { message: 'Already confirmed as co-host' } };
        }
        
        // Update existing record
        const { error } = await supabase
          .from('event_hosts')
          .update({ 
            status: 'accepted',
            responded_at: new Date().toISOString(),
            user_id: userProfile.id,
            name: userProfile.name || existing.name,
            avatar_url: userProfile.avatar_url || existing.avatar_url
          })
          .eq('id', existing.id);

        return { error };
      }

      // If no existing record found, create new one (shouldn't happen in normal flow)
      const { error } = await supabase
        .from('event_hosts')
        .insert([{
          event_id: eventId,
          user_id: userProfile.id,
          email: userEmail,
          name: userProfile.name || userEmail.split('@')[0],
          avatar_url: userProfile.avatar_url,
          status: 'accepted',
          is_main_host: false,
          invited_at: new Date().toISOString(),
          responded_at: new Date().toISOString()
        }]);

      return { error };
    } catch (error) {
      return { error };
    }
  }

  /**
   * Invitar usuario a un evento - CORREGIDO
   */
  static async addUserToEvent(eventId: any, user:any, userId: any) {
    try {
      const { error } = await supabase
        .from('event_users')
        .insert([{
          event_id: eventId,
          user_id: userId,
          ...user,
          invitation_status: 'invited',
          approval_status: 'pending'
        }]);

      if (error) {
        return { error };
      }

      return { data: { event_id: eventId, user_id: userId }, error: null };
    } catch (error) {
      return { error };
    }
  }

  static async inviteUser(
    eventId: string,
    userData: { email: string; name?: string; message?: string },
    inviterId: string | 'system'
  ): Promise<{ data: EventUser | null; error: any }> {
    try {
      // Solo verificar permisos si NO es una invitación del sistema (registro desde calendario)
      if (inviterId !== 'system') {
        const permissions = await this.getUserEventPermissions(eventId, inviterId);
        if (!permissions.canManageAttendees) {
          return { data: null, error: { message: 'No tienes permisos para invitar usuarios' } };
        }
      }

      // Verificar si ya está invitado
      const { data: existing } = await supabase
        .from('event_users')
        .select('*')
        .eq('event_id', eventId)
        .eq('email', userData.email)
        .maybeSingle();

      if (existing) {
        return { data: null, error: { message: 'Este email ya ha sido invitado' } };
      }

      // Resto del código permanece igual...
      // Obtener detalles del evento
      const { data: event } = await this.getById(eventId);
      if (!event) {
        return { data: null, error: { message: 'Evento no encontrado' } };
      }

      // Verificar límite de plazas
      let isOnWaitingList = false;
      let waitingListPosition: number | undefined;

      if (event.limited_places) {
        const { data: confirmedUsers } = await supabase
          .from('event_users')
          .select('id')
          .eq('event_id', eventId)
          .eq('rsvp_status', 'confirmed')
          .eq('is_on_waiting_list', false);

        const confirmedCount = confirmedUsers?.length || 0;

        if (confirmedCount >= event.limited_places) {
          isOnWaitingList = true;
          
          // Obtener posición en waiting list
          const { data: waitingListUsers } = await supabase
            .from('event_users')
            .select('waiting_list_position')
            .eq('event_id', eventId)
            .eq('is_on_waiting_list', true)
            .order('waiting_list_position', { ascending: false })
            .limit(1);

          waitingListPosition = (waitingListUsers?.[0]?.waiting_list_position || 0) + 1;
        }
      }

      // Generar token de invitación usando EmailService
      const { data: tokenData, error: tokenError } = await EmailService.generateEventUserToken(
        eventId,
        userData.email,
        userData.name || userData.email.split('@')[0]
      );

      if (tokenError || !tokenData) {
        return { data: null, error: { message: 'Error generating invitation token' } };
      }

      const invitationToken = tokenData.token;

      // Crear registro de usuario del evento
      const { data: eventUser, error } = await supabase
        .from('event_users')
        .insert([{
          event_id: eventId,
          email: userData.email,
          name: userData.name || userData.email.split('@')[0],
          invitation_status: 'invited',
          approval_status: event.requires_approval ? 'pending' : 'approved',
          rsvp_status: 'pending',
          attendance_status: 'pending',
          is_on_waiting_list: isOnWaitingList,
          waiting_list_position: waitingListPosition,
          invited_at: new Date().toISOString(),
          invitation_token: invitationToken,
          invitation_message: userData.message,
          invited_by: inviterId === 'system' ? null : inviterId
        }])
        .select()
        .single();

      if (error) {
        return { data: null, error };
      }

      // Enviar email de invitación usando EmailService
      try {
        await EmailService.sendEventUserInvitation(
          userData.email,
          {
            id: event.id,
            title: event.title,
            start_date: event.start_date,
            start_time: event.start_time || '00:00',
            location: event.location || '',
            host_name: event.hosts?.[0]?.name || 'Event Host',
            invitation_token: invitationToken,
            is_on_waiting_list: isOnWaitingList,
            waiting_list_position: waitingListPosition,
            custom_message: userData.message
          }
        );
      } catch (emailError) {
        console.error('Error sending invitation email:', emailError);
        // Continuar aunque falle el email
      }

      return { data: eventUser, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Procesar waiting list cuando se libera una plaza - CORREGIDO
   */
  static async processWaitingList(eventId: string): Promise<void> {
    try {
      const { data: event } = await this.getById(eventId);
      if (!event?.limited_places) return;

      // Contar usuarios confirmados no en waiting list
      const { data: confirmedUsers } = await supabase
        .from('event_users')
        .select('id')
        .eq('event_id', eventId)
        .eq('rsvp_status', 'confirmed')
        .eq('is_on_waiting_list', false);

      const confirmedCount = confirmedUsers?.length || 0;
      const availableSpots = event.limited_places - confirmedCount;

      if (availableSpots > 0) {
        // Obtener usuarios de waiting list
        const { data: waitingUsers } = await supabase
          .from('event_users')
          .select('*')
          .eq('event_id', eventId)
          .eq('is_on_waiting_list', true)
          .eq('rsvp_status', 'confirmed')
          .order('waiting_list_position', { ascending: true })
          .limit(availableSpots);

        // Mover usuarios de waiting list a confirmados
        for (const user of waitingUsers || []) {
          await supabase
            .from('event_users')
            .update({
              is_on_waiting_list: false,
              waiting_list_position: null,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);

          // Enviar notificación usando EmailService
          try {
            await EmailService.sendWaitingListNotification(
              user.email,
              {
                id: event.id,
                title: event.title,
                start_date: event.start_date,
                start_time: event.start_time || '00:00',
                location: event.location || ''
              }
            );
          } catch (error) {
            console.error('Error sending waiting list notification:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error processing waiting list:', error);
    }
  }

  // ELIMINAR estos métodos del EventService - van en EmailService:
  // - sendEventUserInvitation
  // - sendWaitingListNotification  
  // - generateEventUserToken

  /**
   * Aceptar invitación a evento
   */
  static async acceptEventUserInvitation(
    eventId: string,
    token: string,
    userProfile?: { id: string; name?: string; avatar_url?: string }
  ): Promise<{ data: EventUser | null; error: any }> {
    try {
      // Buscar por token
      const { data: eventUser, error: findError } = await supabase
        .from('event_users')
        .select('*')
        .eq('event_id', eventId)
        .eq('invitation_token', token)
        .eq('invitation_status', 'invited')
        .maybeSingle();

      if (findError || !eventUser) {
        return { data: null, error: { message: 'Invalid or expired invitation link' } };
      }

      // Verificar si ya fue aceptada
      if (eventUser.invitation_status === 'accepted') {
        return { data: eventUser, error: { message: 'Invitation already accepted' } };
      }

      // Actualizar estado
      const updateData: Partial<EventUser> = {
        invitation_status: 'accepted',
        rsvp_status: 'confirmed',
        responded_at: new Date().toISOString()
      };

      if (userProfile) {
        updateData.user_id = userProfile.id;
        updateData.name = userProfile.name || eventUser.name;
        updateData.avatar_url = userProfile.avatar_url;
      }

      const { data: updatedUser, error: updateError } = await supabase
        .from('event_users')
        .update(updateData)
        .eq('id', eventUser.id)
        .select()
        .single();

      if (updateError) {
        return { data: null, error: updateError };
      }

      // Si estaba en waiting list, verificar si puede pasar a confirmado
      if (eventUser.is_on_waiting_list) {
        await this.processWaitingList(eventId);
      }

      return { data: updatedUser, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Obtener usuarios del evento con filtros
   */
  static async getEventUsers(
    eventId: string, 
    filters?: EventUserFilters
  ): Promise<{ data: EventUser[] | null; error: any }> {
    try {
      let query = supabase
        .from('event_users')
        .select('*')
        .eq('event_id', eventId);

      // Aplicar filtros
      if (filters?.invitation_status) {
        query = query.eq('invitation_status', filters.invitation_status);
      }
      if (filters?.approval_status) {
        query = query.eq('approval_status', filters.approval_status);
      }
      if (filters?.rsvp_status) {
        query = query.eq('rsvp_status', filters.rsvp_status);
      }
      if (filters?.attendance_status) {
        query = query.eq('attendance_status', filters.attendance_status);
      }
      if (filters?.is_on_waiting_list !== undefined) {
        query = query.eq('is_on_waiting_list', filters.is_on_waiting_list);
      }
      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query
        .order('invited_at', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Actualizar estado de aprobación de un usuario
   */
  static async updateEventUserApproval(
    eventUserId: string,
    approvalStatus: 'approved' | 'rejected',
    approverId: string
  ): Promise<{ data: EventUser | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('event_users')
        .update({
          approval_status: approvalStatus,
          approved_by: approverId,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', eventUserId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Eliminar usuario del evento
   */
  static async removeEventUser(
    eventUserId: string,
    removerId: string
  ): Promise<{ error: any }> {
    try {
      // Verificar permisos
      const { data: eventUser } = await supabase
        .from('event_users')
        .select('event_id')
        .eq('id', eventUserId)
        .single();

      if (!eventUser) {
        return { error: { message: 'Event user not found' } };
      }

      const permissions = await this.getUserEventPermissions(eventUser.event_id, removerId);
      if (!permissions.canManageAttendees) {
        return { error: { message: 'No tienes permisos para eliminar usuarios' } };
      }

      const { error } = await supabase
        .from('event_users')
        .delete()
        .eq('id', eventUserId);

      return { error };
    } catch (error) {
      return { error };
    }
  }

  /**
   * Permite al usuario autenticado dejar de asistir (eliminar su propia fila en event_users)
   */
  static async leaveEventAsUser(
    eventId: string,
    userId: string
  ): Promise<{ success: boolean; error: any }> {
    try {
      // Buscar la fila del usuario en event_users para ese evento
      const { data: eventUser, error: findErr } = await supabase
        .from('event_users')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .maybeSingle();

      if (findErr) {
        return { success: false, error: findErr };
      }

      if (!eventUser) {
        // Nada que eliminar
        return { success: true, error: null };
      }

      // Intentar borrar la fila
      const { error: delErr } = await supabase
        .from('event_users')
        .delete()
        .eq('id', eventUser.id);

      if (!delErr) {
        // Si había límite de plazas, procesar waiting list
        await this.processWaitingList(eventId).catch(() => undefined);
        return { success: true, error: null };
      }

      // Fallback: si no se pudo borrar (p.ej., política RLS), marcar como declined
      const { error: updErr } = await supabase
        .from('event_users')
        .update({
          rsvp_status: 'declined',
          invitation_status: 'declined',
          is_on_waiting_list: false,
          waiting_list_position: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventUser.id);

      if (!updErr) {
        await this.processWaitingList(eventId).catch(() => undefined);
        return { success: true, error: null };
      }

      return { success: false, error: delErr || updErr };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Remover usuario de la waiting list y moverlo a la lista principal
   */
  static async removeFromWaitingList(
    eventUserId: string
  ): Promise<{ data: EventUser | null; error: any }> {
    try {
      // Obtener el usuario actual
      const { data: eventUser, error: findError } = await supabase
        .from('event_users')
        .select('*')
        .eq('id', eventUserId)
        .single();

      if (findError || !eventUser) {
        return { data: null, error: { message: 'Event user not found' } };
      }

      // Verificar que esté en waiting list
      if (!eventUser.is_on_waiting_list) {
        return { data: null, error: { message: 'User is not on waiting list' } };
      }

      // Obtener detalles del evento para verificar si requiere aprobación
      const { data: event } = await this.getById(eventUser.event_id);
      if (!event) {
        return { data: null, error: { message: 'Event not found' } };
      }

      // Actualizar el usuario: sacarlo de waiting list
      const updateData: Partial<EventUser> = {
        is_on_waiting_list: false,
        waiting_list_position: undefined,
        updated_at: new Date().toISOString()
      };

      // Si el evento requiere aprobación, mantener como pending
      // Si no requiere aprobación, aprobar automáticamente
      if (!event.requires_approval && eventUser.approval_status === 'pending') {
        updateData.approval_status = 'approved';
        updateData.approved_at = new Date().toISOString();
      }

      const { data: updatedUser, error: updateError } = await supabase
        .from('event_users')
        .update(updateData)
        .eq('id', eventUserId)
        .select()
        .single();

      if (updateError) {
        return { data: null, error: updateError };
      }

      // Reordenar las posiciones de la waiting list
      await this.reorderWaitingList(eventUser.event_id);

      return { data: updatedUser, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Reordenar posiciones de la waiting list después de remover un usuario
   */
  static async reorderWaitingList(eventId: string): Promise<void> {
    try {
      // Obtener todos los usuarios en waiting list ordenados por posición
      const { data: waitingUsers } = await supabase
        .from('event_users')
        .select('id, waiting_list_position')
        .eq('event_id', eventId)
        .eq('is_on_waiting_list', true)
        .order('waiting_list_position', { ascending: true });

      if (!waitingUsers || waitingUsers.length === 0) return;

      // Reordenar posiciones secuencialmente
      for (let i = 0; i < waitingUsers.length; i++) {
        const newPosition = i + 1;
        if (waitingUsers[i].waiting_list_position !== newPosition) {
          await supabase
            .from('event_users')
            .update({ waiting_list_position: newPosition })
            .eq('id', waitingUsers[i].id);
        }
      }
    } catch (error) {
      console.error('Error reordering waiting list:', error);
    }
  }

  /**
   * Obtener eventos donde el usuario participa (event_users)
   */
  static async getUserEvents(userId: string) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          event_users!inner(
            user_id,
            rsvp_status,
            is_on_waiting_list,
            approval_status
          )
        `)
        .eq('event_users.user_id', userId)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching user events:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Unexpected error:', error);
      return { data: null, error };
    }
  }

  /**
   * Buscar eventos y usuarios por texto
   */
  static async searchAll(
    searchTerm: string,
    filters: EventFilters = {}
  ): Promise<{ 
    data: { events: EventWithRelations[]; users: any[] } | null; 
    error: any
  }> {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return { data: { events: [], users: [] }, error: null };
      }

      const cleanSearchTerm = searchTerm.trim();

      // 1) Buscar eventos por texto (ya incluye filtros básicos)
      const { data: eventsByText, error: eventsError } = await this.searchEvents(cleanSearchTerm, filters);
      if (eventsError) {
        return { data: null, error: eventsError };
      }

      // 2) Buscar hosts por nombre/email y obtener event_ids
      const { data: hostMatches, error: hostsSearchError } = await supabase
        .from('event_hosts')
        .select('event_id')
        .or(`name.ilike.%${cleanSearchTerm}%,email.ilike.%${cleanSearchTerm}%`)
        .limit(200);

      if (hostsSearchError) {
        console.warn('Error searching hosts:', hostsSearchError);
      }

      const idsFromText = new Set((eventsByText || []).map(e => e.id));
      const hostEventIds = Array.from(
        new Set((hostMatches || []).map(h => h.event_id).filter(Boolean) as string[])
      ).filter(id => !idsFromText.has(id as string)) as string[];

      // 3) Traer eventos por IDs encontrados en hosts y aplicar filtros
      let eventsByHosts: EventRow[] = [];
      if (hostEventIds.length > 0) {
        let evQuery = supabase.from('events').select('*').in('id', hostEventIds);

        if (filters.category) evQuery = evQuery.eq('category', filters.category);
        if (filters.type) evQuery = evQuery.eq('type', filters.type);
        if (filters.modality) evQuery = evQuery.eq('modality', filters.modality);
        if (filters.is_public !== undefined) evQuery = evQuery.eq('is_public', filters.is_public);
        if (filters.start_date_from) evQuery = evQuery.gte('start_date', filters.start_date_from);
        if (filters.start_date_to) evQuery = evQuery.lte('start_date', filters.start_date_to);
        if (filters.parent_event_id !== undefined) {
          if (filters.parent_event_id === null) evQuery = evQuery.is('parent_event_id', null);
          else evQuery = evQuery.eq('parent_event_id', filters.parent_event_id);
        }

        const { data: hostEventsData, error: hostEventsError } = await evQuery.limit(200);
        if (hostEventsError) {
          console.warn('Error fetching events by host search:', hostEventsError);
        } else {
          eventsByHosts = hostEventsData || [];
        }
      }

      // 4) Unificar resultados y cargar hosts para todos
      const combined: EventRow[] = [
        ...(((eventsByText as unknown as EventRow[]) || [])),
        ...eventsByHosts
      ];

      const uniqueMap = new Map<string, EventRow>();
      for (const e of combined) uniqueMap.set(e.id, e);
      const uniqueEvents = Array.from(uniqueMap.values());

      const allEventIds = uniqueEvents.map(e => e.id);
      const { data: allHosts } = await supabase
        .from('event_hosts')
        .select('*')
        .in('event_id', allEventIds);

      const eventsWithRelations: any[] = uniqueEvents
        .map(e => ({
          ...e,
          hosts: allHosts?.filter(h => h.event_id === e.id) || [],
          side_events: [],
          hosts_count: allHosts?.filter(h => h.event_id === e.id).length || 0,
          attendees_count: 0
        }))
        .sort((a, b) =>
          (a.start_date || '').localeCompare(b.start_date || '') ||
          (a.start_time || '').localeCompare(b.start_time || '')
        );

      // 5) Buscar usuarios en profiles
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, email')
        .or(`full_name.ilike.%${cleanSearchTerm}%,email.ilike.%${cleanSearchTerm}%`)
        .limit(20);

      if (usersError) {
        console.warn('Error searching users:', usersError);
      }

      const formattedUsers = (users || []).map(user => ({
        id: user.id,
        name: user.full_name || user.email || 'Unknown User',
        avatar: user.avatar_url || ''
      }));

      return {
        data: {
          events: eventsWithRelations,
          users: formattedUsers
        },
        error: null
      };
    } catch (error) {
      console.error('Error in searchAll:', error);
      return { data: null, error };
    }
  }

  /**
   * Buscar eventos por texto mejorado
   */
  static async searchEvents(
    searchTerm: string,
    filters: EventFilters = {},
    limit = 20
  ): Promise<{ data: EventWithRelations[] | null; error: any }> {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return { data: [], error: null };
      }

      const cleanSearchTerm = searchTerm.trim();
      
      let query = supabase
        .from('events')
        .select('*')
        // Nota: columnas enum (category/type) no soportan ILIKE, evitamos castear desde el cliente.
        .or(`title.ilike.%${cleanSearchTerm}%,description.ilike.%${cleanSearchTerm}%,location.ilike.%${cleanSearchTerm}%`);

      // Aplicar filtros adicionales
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.modality) {
        query = query.eq('modality', filters.modality);
      }
      if (filters.is_public !== undefined) {
        query = query.eq('is_public', filters.is_public);
      }
      if (filters.start_date_from) {
        query = query.gte('start_date', filters.start_date_from);
      }
      if (filters.start_date_to) {
        query = query.lte('start_date', filters.start_date_to);
      }

      const { data: events, error: eventsError } = await query
        .order('start_date', { ascending: true })
        .limit(limit);

      if (eventsError) {
        return { data: null, error: eventsError };
      }

      if (!events || events.length === 0) {
        return { data: [], error: null };
      }

      // Obtener hosts para todos los eventos
      const eventIds = events.map(event => event.id);
      const { data: allHosts } = await supabase
        .from('event_hosts')
        .select('*')
        .in('event_id', eventIds);

      // Construir eventos con relaciones
      const eventsWithRelations: EventWithRelations[] = events.map(event => ({
        ...event,
        hosts: allHosts?.filter(host => host.event_id === event.id) || [],
        side_events: [],
        hosts_count: allHosts?.filter(host => host.event_id === event.id).length || 0,
        attendees_count: 0
      }));

      return { data: eventsWithRelations, error: null };
    } catch (error) {
      console.error('Error in searchEvents:', error);
      return { data: null, error };
    }
  }

  /**
   * Obtener participantes del evento (event_users) con filtros
   */
  static async getEventParticipants(
    eventId: string,
    filters?: EventUserFilters
  ): Promise<{ data: EventUser[] | null; error: any }> {
    try {
      let query = supabase
        .from('event_users')
        .select('*')
        .eq('event_id', eventId);

      // Aplicar filtros
      if (filters?.invitation_status) {
        query = query.eq('invitation_status', filters.invitation_status);
      }
      if (filters?.approval_status) {
        query = query.eq('approval_status', filters.approval_status);
      }
      if ( filters?.rsvp_status) {
        query = query.eq('rsvp_status', filters.rsvp_status);
      }
      if (filters?.attendance_status) {
        query = query.eq('attendance_status', filters.attendance_status);
      }
      if (filters?.is_on_waiting_list !== undefined) {
        query = query.eq('is_on_waiting_list', filters.is_on_waiting_list);
      }
      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query
        .order('invited_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching event participants:', error);
      return { data: null, error };
    }
  }

  static async requestSideEventLink(mainEventId: string, sideEventId: string, invitationMessage?: string) {
    const { data, error } = await supabase
      .rpc('request_side_event_link', {
        p_main_event_id: mainEventId,
        p_side_event_id: sideEventId,
        p_invitation_message: invitationMessage || null
      });
    return { data, error };
  }

  static async decideSideEventLink(linkId: string, decision: 'accepted' | 'rejected') {
    const { data, error } = await supabase
      .rpc('decide_side_event_link', { p_link_id: linkId, p_decision: decision });
    return { data, error };
  }

  static async cancelSideEventLink(linkId: string) {
    const { data, error } = await supabase
      .rpc('cancel_side_event_link', { p_link_id: linkId });
    return { data, error };
  }

  // Outgoing (este evento es main_event_id) - sin joins automáticos (evitamos error FK)
  static async getOutgoingSideLinks(mainEventId: string) {
    const { data, error } = await supabase
      .from('event_side_links')
      .select(`
        id,
        status,
        invitation_message,
        requested_by,
        decided_by,
        accepted_at,
        rejected_at,
        cancelled_at,
        side_event_id
      `)
      .eq('main_event_id', mainEventId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error };

    const sideIds = Array.from(new Set((data ?? []).map(r => r.side_event_id)));
    const profileIds = Array.from(new Set(
      (data ?? []).flatMap(r => [r.requested_by, r.decided_by].filter(Boolean) as string[])
    ));

    const [{ data: eventsData }, { data: profilesData }] = await Promise.all([
      sideIds.length
        ? supabase.from('events').select('id,title,start_date').in('id', sideIds)
        : Promise.resolve({ data: [] }),
      profileIds.length
        ? supabase.from('profiles').select('id, full_name, avatar_url').in('id', profileIds)
        : Promise.resolve({ data: [] })
    ]);

    const mapEvents: Record<string, any> = Object.fromEntries((eventsData || []).map(e => [e.id, e]));
    const mapProfiles: Record<string, any> = Object.fromEntries((profilesData || []).map(p => [p.id, p]));

    const enriched = (data || []).map(r => ({
      id: r.id,
      status: r.status,
      invitation_message: r.invitation_message,
      requested_by: r.requested_by,
      decided_by: r.decided_by,
      accepted_at: r.accepted_at,
      rejected_at: r.rejected_at,
      cancelled_at: r.cancelled_at,
      side_event: mapEvents[r.side_event_id] || { id: r.side_event_id, title: 'Evento', start_date: '' },
      requester: mapProfiles[r.requested_by] || { id: r.requested_by },
      decider: r.decided_by ? (mapProfiles[r.decided_by] || { id: r.decided_by }) : null
    }));

    return { data: enriched, error: null };
  }

  // Incoming pending (este evento es side_event_id) - sin join automático
  static async getIncomingSideLinkRequests(eventId: string) {
    const { data, error } = await supabase
      .from('event_side_links')
      .select(`
        id,
        status,
        invitation_message,
        requested_by,
        decided_by,
        created_at,
        main_event_id
      `)
      .eq('side_event_id', eventId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) return { data: null, error };

    const mainIds = Array.from(new Set((data ?? []).map(r => r.main_event_id)));
    const profileIds = Array.from(new Set((data ?? []).map(r => r.requested_by)));

    const [{ data: eventsData }, { data: profilesData }] = await Promise.all([
      mainIds.length
        ? supabase.from('events').select('id,title,start_date').in('id', mainIds)
        : Promise.resolve({ data: [] }),
      profileIds.length
        ? supabase.from('profiles').select('id, full_name, avatar_url').in('id', profileIds)
        : Promise.resolve({ data: [] })
    ]);

    const mapEvents: Record<string, any> = Object.fromEntries((eventsData || []).map(e => [e.id, e]));
    const mapProfiles: Record<string, any> = Object.fromEntries((profilesData || []).map(p => [p.id, p]));

    const enriched = (data || []).map(r => ({
      id: r.id,
      status: r.status,
      invitation_message: r.invitation_message,
      requested_by: r.requested_by,
      created_at: r.created_at,
      main_event: mapEvents[r.main_event_id] || { id: r.main_event_id, title: 'Evento', start_date: '' },
      requester: mapProfiles[r.requested_by] || { id: r.requested_by }
    }));

    return { data: enriched, error: null };
  }

  // General search de eventos (remueve restricción de owner previo)
  static async searchEventsLinking(term: string) {
    const { data, error } = await supabase
      .from('events')
      .select('id, title, start_date')
      .ilike('title', `%${term}%`)
      .limit(25);
    return { data, error };
  }

}